import { c as _c } from "react/compiler-runtime";
import { TargetSelector, TargetSelectorUi, type TargetSelectorMode } from '../TargetSelector';
import { FrontWeightDesignMode, type FrontWeightDesignTarget } from '@/lib/piano/touch-design/design/front-weight-design';
import { type FrontWeightDesignProps } from './FrontWeightDesign.types';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { DesignLayout } from '../DesignLayout';
import { formatGrams, formatSignedGrams } from '@/lib/format/format-weight';
import { HourglassIcon } from 'lucide-react';
import { useStrikeWeightDesign } from '@/hooks/store/use-design-store';
import { FrontWeightChart } from '@/components/charts/FrontWeightChart';
import { useFrontWeightRecommendation } from './hooks/use-front-weight-recommendation';
import { useFrontWeightTargetSelector } from './hooks/use-frontweight-target-selector';
import { useTranslation } from '@/hooks/use-translation';
import { useCallback, useMemo } from 'react';
import { StrikeWeightDesignMode } from '@/lib/piano/touch-design/design/strike-weight-design';
import { useTargetSeries } from '../hooks/use-target-series';
import { useTargetReading } from '../hooks/use-target-reading';
import { benchAdjustment } from '@/lib/piano/touch-design/bench-adjustment';
import { NotEnoughData } from '../NotEnoughData';
export const FrontWeightDesign: React.FC<FrontWeightDesignProps> = t0 => {
  const $ = _c(74);
  const {
    analyzedKeyboard,
    requiredDataPercentage,
    notEnoughDataErrorTitle,
    notEnoughDataErrorDescription
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    frontWeightDesignMode,
    frontWeightDesignTarget,
    onModeChange,
    onTargetChange,
    updateFrontWeightDesign
  } = useFrontWeightTargetSelector();
  const {
    strikeWeightDesignMode
  } = useStrikeWeightDesign();
  useFrontWeightRecommendation(frontWeightDesignMode, frontWeightDesignTarget, updateFrontWeightDesign);
  const {
    frontWeightTargetSerie: targetSerie
  } = useTargetSeries();
  let t1;
  if ($[0] !== analyzedKeyboard || $[1] !== targetSerie) {
    t1 = {
      difference: benchAdjustment,
      format: formatGrams,
      formatDifference: formatSignedGrams,
      keyboard: analyzedKeyboard,
      targetSerie
    };
    $[0] = analyzedKeyboard;
    $[1] = targetSerie;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const renderReading = useTargetReading(t1);
  let t2;
  if ($[3] !== analyzedKeyboard) {
    t2 = analyzedKeyboard.mapToArray(_temp).filter(_temp2);
    $[3] = analyzedKeyboard;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const hasEnoughInputData = t2.length >= Math.round(analyzedKeyboard.size * requiredDataPercentage);
  let t3;
  if ($[5] !== t) {
    t3 = value => t("FW#{{value}}", {
      value
    });
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const frontWeightCurveLabelFormatter = t3;
  let t4;
  if ($[7] !== t) {
    t4 = value_0 => t("{{value}} g", {
      value: value_0
    });
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const balanceWeightLabelFormatter = t4;
  const shouldDisableComputedMode = strikeWeightDesignMode === StrikeWeightDesignMode.Computed;
  let t5;
  if ($[9] !== shouldDisableComputedMode || $[10] !== t) {
    t5 = shouldDisableComputedMode ? t("The automatic computation is already active on the strike weight design.") : undefined;
    $[9] = shouldDisableComputedMode;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const reason = t5;
  let t6;
  if ($[12] !== reason || $[13] !== shouldDisableComputedMode) {
    t6 = {
      computedModeDisabledReason: reason,
      shouldDisableComputedMode
    };
    $[12] = reason;
    $[13] = shouldDisableComputedMode;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const {
    shouldDisableComputedMode: shouldDisableComputedMode_0,
    computedModeDisabledReason
  } = t6;
  let t7;
  if ($[15] !== t) {
    t7 = t("Use front weight measured values");
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t) {
    t8 = t("Keep the front weight as is.");
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      selectorUi: TargetSelectorUi.UniqueTargetSelector,
      target: null
    };
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t7 || $[21] !== t8) {
    t10 = {
      description: t7,
      label: t8,
      options: t9,
      value: FrontWeightDesignMode.AsMeasured
    };
    $[20] = t7;
    $[21] = t8;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t) {
    t11 = t("The front weight will be computed automatically in order to achieve the specified balance weight.");
    $[23] = t;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t) {
    t12 = t("Uses the springs, the strike weight and the ratio set in steps 1, 3 and 4.");
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t) {
    t13 = t("Compute automatically");
    $[27] = t;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t) {
    t14 = t("Target Balance Weight");
    $[29] = t;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== balanceWeightLabelFormatter || $[32] !== t14) {
    t15 = {
      labelFormatter: balanceWeightLabelFormatter,
      maxValue: 60,
      minValue: 30,
      placeholder: t14,
      selectorUi: TargetSelectorUi.NumericSelector,
      step: 1
    };
    $[31] = balanceWeightLabelFormatter;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== computedModeDisabledReason || $[35] !== shouldDisableComputedMode_0 || $[36] !== t11 || $[37] !== t12 || $[38] !== t13 || $[39] !== t15) {
    t16 = {
      description: t11,
      disabledReason: computedModeDisabledReason,
      formula: "FW = WBW + R \xD7 SW \u2212 BW \u2212 BWS",
      hint: t12,
      isDisabled: shouldDisableComputedMode_0,
      label: t13,
      options: t15,
      value: FrontWeightDesignMode.Computed
    };
    $[34] = computedModeDisabledReason;
    $[35] = shouldDisableComputedMode_0;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t) {
    t17 = t("Pick a target among the standard front weight curves, from FW#1 to FW#12.");
    $[41] = t;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t) {
    t18 = t("Use standard curves");
    $[43] = t;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t) {
    t19 = t("Target curve");
    $[45] = t;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== frontWeightCurveLabelFormatter || $[48] !== t19) {
    t20 = {
      labelFormatter: frontWeightCurveLabelFormatter,
      maxCharacters: 8,
      maxValue: 12,
      minValue: 1,
      placeholder: t19,
      selectorUi: TargetSelectorUi.NumericSelector,
      step: 1
    };
    $[47] = frontWeightCurveLabelFormatter;
    $[48] = t19;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== t17 || $[51] !== t18 || $[52] !== t20) {
    t21 = {
      description: t17,
      label: t18,
      options: t20,
      value: FrontWeightDesignMode.StandardCurves
    };
    $[50] = t17;
    $[51] = t18;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t10 || $[55] !== t16 || $[56] !== t21) {
    t22 = [t10, t16, t21];
    $[54] = t10;
    $[55] = t16;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  const targetSelectorModes = t22;
  let t23;
  if ($[58] !== targetSerie?.data) {
    t23 = targetSerie?.data?.some(_temp3);
    $[58] = targetSerie?.data;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  const hasEnoughOutputData = t23;
  const shouldDisplayComputeModeWaitingMessage = frontWeightDesignMode === FrontWeightDesignMode.Computed && !hasEnoughOutputData;
  let t24;
  if ($[60] !== analyzedKeyboard || $[61] !== frontWeightDesignMode || $[62] !== frontWeightDesignTarget || $[63] !== hasEnoughInputData || $[64] !== notEnoughDataErrorDescription || $[65] !== notEnoughDataErrorTitle || $[66] !== onModeChange || $[67] !== onTargetChange || $[68] !== renderReading || $[69] !== shouldDisplayComputeModeWaitingMessage || $[70] !== t || $[71] !== targetSelectorModes || $[72] !== targetSerie) {
    t24 = !hasEnoughInputData ? <NotEnoughData title={notEnoughDataErrorTitle} description={notEnoughDataErrorDescription} /> : <DesignLayout objective={<TargetSelector title={t("Target")} modes={targetSelectorModes} selectedMode={frontWeightDesignMode} selectedTarget={frontWeightDesignTarget} onModeChange={onModeChange} onTargetChange={onTargetChange} />} chart={shouldDisplayComputeModeWaitingMessage ? <Alert variant="default"><HourglassIcon /><AlertTitle>{t("Waiting for remaining design targets to be specified.")}</AlertTitle><AlertDescription>{t("The front weight will be computed when all other design targets will be specified.")}</AlertDescription></Alert> : <FrontWeightChart renderReading={renderReading} variant="design" keyboard={analyzedKeyboard} targetSerie={targetSerie ?? undefined} extraLegendItems={targetSerie ? [{
      colorClassName: "text-ptl-series-target",
      id: "target",
      label: t("Target"),
      mark: "line-thick" as const
    }] : undefined} />} />;
    $[60] = analyzedKeyboard;
    $[61] = frontWeightDesignMode;
    $[62] = frontWeightDesignTarget;
    $[63] = hasEnoughInputData;
    $[64] = notEnoughDataErrorDescription;
    $[65] = notEnoughDataErrorTitle;
    $[66] = onModeChange;
    $[67] = onTargetChange;
    $[68] = renderReading;
    $[69] = shouldDisplayComputeModeWaitingMessage;
    $[70] = t;
    $[71] = targetSelectorModes;
    $[72] = targetSerie;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  return t24;
};
function _temp(key) {
  return key.payload.frontWeight;
}
function _temp2(v) {
  return v !== undefined && v !== null;
}
function _temp3(key_0) {
  return key_0?.payload;
}